import { useEffect, useRef, useState } from "react";

export type ShopCoverData = { bannerUrl?: string | null; coverMode?: string; coverAnimationUrl?: string | null; coverPosterUrl?: string | null; coverVideoUrl?: string | null };
type CoverPlayer = { priority: number; visibility: number; visible: boolean; mediaKey: string; fallback: boolean; select: (playing: boolean, prepared: boolean, mirror: boolean, fallback: boolean) => void };
const players = new Map<object, CoverPlayer>();
const sharedVideos = new Map<string, HTMLVideoElement>();
function selectVisiblePlayers() {
  const ranked = [...players.entries()].sort(([, a], [, b]) => b.priority - a.priority || b.visibility - a.visibility);
  const selected = new Set<object>(), prepared = new Set<object>(), media = new Set<string>();
  // A recent-items rail and main card can refer to the same film. Only the
  // highest-priority instance should fetch it, including during preloading.
  for (const [key, player] of ranked) {
    if (!player.visible || selected.size >= 2 || media.has(player.mediaKey)) continue;
    selected.add(key); prepared.add(key); media.add(player.mediaKey);
  }
  let warmCount = 0;
  for (const [key, player] of ranked) {
    if (prepared.has(key) || media.has(player.mediaKey) || warmCount >= 2) continue;
    prepared.add(key); media.add(player.mediaKey); warmCount++;
  }
  const activeMedia = new Map([...players.entries()].filter(([key]) => selected.has(key)).map(([, player]) => [player.mediaKey, player]));
  for (const [key, player] of players) player.select(selected.has(key), prepared.has(key), player.visible && !selected.has(key) && activeMedia.has(player.mediaKey), activeMedia.get(player.mediaKey)?.fallback ?? false);
}

/** Two visible players and two nearby, paused preloads. Photos always remain available. */
export function ShopCover({ store, className, alt = "", priority = 0 }: { store: ShopCoverData; className?: string; alt?: string; priority?: number }) {
  const root = useRef<HTMLSpanElement>(null), video = useRef<HTMLVideoElement>(null), mirror = useRef<HTMLCanvasElement>(null), slot = useRef({});
  const [mirroring, setMirroring] = useState(false), [mirrorFallback, setMirrorFallback] = useState(false);
  const [playing, setPlaying] = useState(false), [prepared, setPrepared] = useState(false);
  const [failed, setFailed] = useState(false), [videoFailed, setVideoFailed] = useState(false);
  const animated = store.coverMode === "animation" && !!store.coverAnimationUrl;
  useEffect(() => { setFailed(false); }, [store.coverAnimationUrl]);
  useEffect(() => { setVideoFailed(false); }, [store.coverVideoUrl]);
  useEffect(() => {
    if (!animated || failed || !root.current) { players.delete(slot.current); selectVisiblePlayers(); setPlaying(false); setPrepared(false); setMirroring(false); return; }
    let visible = false, nearby = false, visibility = 0;
    const motion = matchMedia("(prefers-reduced-motion: reduce)");
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    const update = () => {
      const eligible = !document.hidden && !motion.matches && !connection?.saveData;
      const apply = (active: boolean, ready: boolean, copy: boolean, fallback: boolean) => {
        setPrepared(ready);
        setPlaying(active);
        setMirroring(copy); setMirrorFallback(fallback);
      };
      if (!eligible || !nearby) { players.delete(slot.current); selectVisiblePlayers(); apply(false, false, false, false); }
      else {
        players.set(slot.current, { priority, visibility, visible, mediaKey: store.coverVideoUrl || store.coverAnimationUrl!, fallback: !store.coverVideoUrl || videoFailed, select: apply });
        selectVisiblePlayers();
      }
    };
    const observer = new IntersectionObserver(([entry]) => { visibility = entry?.intersectionRatio ?? 0; visible = !!entry?.isIntersecting && visibility >= 0.2; update(); }, { threshold: [0, 0.2, 0.5, 0.75, 1] });
    const ahead = new IntersectionObserver(([entry]) => { nearby = !!entry?.isIntersecting; update(); }, { rootMargin: "800px 0px", threshold: 0 });
    observer.observe(root.current); ahead.observe(root.current);
    document.addEventListener("visibilitychange", update); motion.addEventListener("change", update);
    const interval = window.setInterval(update, 700);
    return () => { observer.disconnect(); ahead.disconnect(); clearInterval(interval); document.removeEventListener("visibilitychange", update); motion.removeEventListener("change", update); players.delete(slot.current); selectVisiblePlayers(); };
  }, [animated, failed, priority, store.coverVideoUrl, store.coverAnimationUrl, videoFailed]);
  useEffect(() => {
    const element = video.current;
    let cancelled = false;
    if (element) {
      if (playing) void element.play().catch(() => { if (!cancelled && element === video.current) setVideoFailed(true); });
      else element.pause();
    }
    return () => { cancelled = true; element?.pause(); };
  }, [playing, prepared, store.coverVideoUrl, videoFailed]);
  useEffect(() => {
    const element = video.current;
    return () => { if (element) { element.pause(); element.removeAttribute("src"); element.load(); } };
  }, [prepared, store.coverVideoUrl, videoFailed]);
  useEffect(() => {
    const element = video.current, key = store.coverVideoUrl;
    if (!playing || !element || !key || videoFailed) return;
    sharedVideos.set(key, element);
    return () => { if (sharedVideos.get(key) === element) sharedVideos.delete(key); };
  }, [playing, prepared, store.coverVideoUrl, videoFailed]);
  useEffect(() => {
    if (!mirroring || mirrorFallback || !store.coverVideoUrl || !mirror.current) return;
    const canvas = mirror.current, context = canvas.getContext("2d"), key = store.coverVideoUrl;
    if (!context) return;
    let frame = 0, previous = 0;
    const draw = (time: number) => {
      const source = sharedVideos.get(key);
      if (time - previous >= 33 && source && source.readyState >= 2 && source.videoWidth && source.videoHeight) {
        if (canvas.width !== source.videoWidth || canvas.height !== source.videoHeight) { canvas.width = source.videoWidth; canvas.height = source.videoHeight; }
        context.drawImage(source, 0, 0, canvas.width, canvas.height);
        previous = time;
      }
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [mirroring, mirrorFallback, store.coverVideoUrl]);
  const still = animated ? store.coverPosterUrl || store.bannerUrl : store.bannerUrl || store.coverPosterUrl;
  return <span ref={root} className={`block relative overflow-hidden ${className ?? ""}`}>
    {still && <img src={still} alt={alt} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />}
    {animated && prepared && !failed && store.coverVideoUrl && !videoFailed && <video ref={video} key={store.coverVideoUrl} src={store.coverVideoUrl} poster={store.coverPosterUrl || still || undefined} aria-hidden muted autoPlay={playing} loop playsInline preload={playing ? "auto" : "metadata"} className="absolute inset-0 w-full h-full object-cover pointer-events-none" style={{ opacity: playing ? 1 : 0 }} onError={() => setVideoFailed(true)} />}
    {animated && mirroring && !mirrorFallback && !failed && <canvas ref={mirror} aria-hidden className="absolute inset-0 w-full h-full object-cover pointer-events-none" />}
    {animated && !failed && ((playing && (!store.coverVideoUrl || videoFailed)) || (mirroring && mirrorFallback)) && <img src={store.coverAnimationUrl!} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover" onError={() => setFailed(true)} />}
  </span>;
}
